/**
 * Metadados VISUAIS dos nós do Comercial nas automações (issue #157):
 * comunicação (template Meta, catálogo, mensagem interna) e lógica de fluxo
 * (variável, de-para, comparar campos, limite, parar se respondeu, loop, esperar
 * evento).
 *
 * Os campos e rótulos vêm do catálogo do backend (comercial_logica.py e
 * comercial_comunicacao.py); aqui ficam só o ícone/tom e o resumo de uma linha
 * do card. Separado de nodeMeta.js (que importa este arquivo) para não misturar
 * com os demais módulos.
 */

export const COMERCIAL_COMUNICACAO_NODE_META = {
  'flow.set_variable': { icon: 'edit', tone: 'slate' },
  'flow.lookup': { icon: 'columns', tone: 'slate' },
  'flow.compare_fields': { icon: 'filter' },
  'flow.rate_limit': { icon: 'timer' },
  'flow.stop_if_replied': { icon: 'close', tone: 'slate' },
  'flow.for_each': { icon: 'refresh', tone: 'slate' },
  'flow.wait_for_event': { icon: 'clock', tone: 'slate' },
  'action.send_whatsapp_template': { icon: 'whatsapp', tone: 'green' },
  'action.send_catalog_item': { icon: 'tag', tone: 'green' },
  'action.send_internal_message': { icon: 'bell' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const UNIDADES = { minutes: 'min', hours: 'h', days: 'dia(s)' }
const OPERADORES = {
  equals: '=', not_equals: '≠', contains: 'contém', not_contains: 'não contém',
  starts_with: 'começa com', ends_with: 'termina com', gt: '>', lt: '<',
}
const EVENTOS = {
  whatsapp_message_received: 'resposta do lead',
  conversation_message_received: 'resposta na conversa',
  deal_stage_changed: 'mudança de etapa',
  deal_closed: 'negócio ganho/perdido',
  lead_tag_added: 'tag adicionada',
}
const FONTES = { deal_items: 'itens do negócio', deal_contacts: 'contatos do negócio' }
const ESCOPOS = { contact: 'lead', deal: 'negócio', automation: 'automação' }

/** Nome curto de um campo "grupo.campo" (usa o rótulo do catálogo quando há). */
function campo(value, options = []) {
  if (!value) return '?'
  if (value.startsWith('var.')) return value.length > 4 ? `variável ${value.slice(4)}` : 'variável ?'
  const label = options.find((o) => o.value === value)?.label
  return label || value
}

/**
 * Resumo de uma linha dos nós do Comercial. Devolve `undefined` para tipos que
 * não são deles (quem chama segue para o resumo dos demais nós).
 */
export function summarizeComercialComunicacao(key, config = {}, type = null) {
  const options = type?.fields?.find((f) => f.type === 'field_ref')?.options || []
  switch (key) {
    case 'flow.set_variable':
      if (!config.name) return 'Defina o nome da variável'
      return `${config.name} ${config.operation === 'add' ? '+=' : '='} ${trunc(config.value || '…', 30)}`
    case 'flow.lookup':
      if (!config.source || !config.variable) return 'Escolha o campo e a variável'
      return `${trunc(campo(config.source, options), 28)} → ${config.variable}`
    case 'flow.compare_fields':
      if (!config.left || !config.operator || !config.right) return 'Escolha os dois campos'
      return `${trunc(campo(config.left, options), 22)} ${OPERADORES[config.operator] || ''} ${trunc(campo(config.right, options), 22)}`
    case 'flow.rate_limit':
      if (!config.max || !config.amount || !config.unit) return 'Defina o limite e o período'
      return `${config.max}× a cada ${config.amount} ${UNIDADES[config.unit] || ''} por ${ESCOPOS[config.scope] || 'lead'}`
    case 'flow.stop_if_replied':
      return 'Encerra se o lead respondeu'
    case 'flow.for_each':
      return config.source ? `Para cada um dos ${FONTES[config.source] || config.source}` : 'Escolha o que percorrer'
    case 'flow.wait_for_event':
      if (!config.event || !config.amount) return 'Escolha o evento e o tempo limite'
      return `Até ${config.amount} ${UNIDADES[config.unit] || ''} pela ${EVENTOS[config.event] || config.event}`
    case 'action.send_whatsapp_template':
      return config.template_name ? `Template ${trunc(config.template_name, 40)}` : 'Informe o template'
    case 'action.send_catalog_item':
      if (config.mode === 'deal_items') return 'Envia os itens do negócio'
      return config.item ? `Envia ${trunc(config.item, 40)}` : 'Escolha o item'
    case 'action.send_internal_message': {
      const n = (config.user_ids || []).length
      const para = n ? `${n} pessoa(s)` : 'responsável'
      return config.message ? `Para ${para}: ${trunc(config.message, 36)}` : 'Escreva a mensagem'
    }
    default:
      return undefined
  }
}
